import React from 'react';
import { View, Pressable, ScrollView, Platform, Dimensions } from 'react-native';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { Text } from '@constants/Themed';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { interceptAppError } from '@utils/errorIntercept';
import Modal from 'react-native-modal';
import { getAdaptiveModalTheme, AdaptiveModalTheme } from '@components/modals/shared/adaptiveModalTheme';
import { responsiveFontSize } from '@utils/responsiveFontSize';

interface ErrorBoundaryProps {
  id?: string;
  children: React.ReactNode;
  onReload?: () => void;
  /** @deprecated Use app theme via Redux; only used when rendered without theme wrapper */
  theme?: boolean;
  showAuthModalOnError?: boolean;
}

interface ErrorBoundaryInnerProps extends ErrorBoundaryProps {
  /** When true use light palette, when false use dark (from app theme) */
  isLightTheme: boolean;
  modalTheme: AdaptiveModalTheme;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  /** Modal can be closed without remounting broken children (avoids reopen loop). */
  modalVisible: boolean;
  remountKey: number;
}

class ErrorBoundaryInner extends React.Component<ErrorBoundaryInnerProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryInnerProps) {
    super(props);
    this.state = { hasError: false, error: null, modalVisible: false, remountKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error, modalVisible: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn(`ErrorBoundary [${this.props.id}] caught an error:`, error);
    if (this.props.showAuthModalOnError !== false) {
      interceptAppError(error, {
        source: this.props.id || 'ErrorBoundary',
        details: {
          componentStack: info?.componentStack,
        },
      });
    }
  }

  /** Close the blocking modal; keep fallback UI so the rest of the app stays usable. */
  handleDismiss = () => {
    this.setState({ modalVisible: false });
  };

  /** Remount children; if the error persists the modal will show again. */
  handleReload = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      modalVisible: false,
      remountKey: prev.remountKey + 1,
    }));
    if (this.props.onReload) {
      this.props.onReload();
    }
  };

  render() {
    if (this.state.hasError) {
      const { modalTheme, id } = this.props;
      const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

      let errorMessage = 'Unknown error';
      if (this.state.error) {
        if (typeof this.state.error === 'string') {
          errorMessage = this.state.error;
        } else if (this.state.error instanceof Error) {
          errorMessage = this.state.error.message || 'Unknown error';
        } else if (this.state.error?.message) {
          errorMessage = String(this.state.error.message);
        } else {
          errorMessage = String(this.state.error);
        }
      }

      const isCSSStyleError =
        errorMessage.includes('CSSStyleDeclaration') && errorMessage.includes('indexed property');

      return (
        <>
          {/* Placeholder in the UI where the error occurred */}
          <View
            style={{
              padding: 20,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: modalTheme.panelSurface,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: modalTheme.panelBorder,
              margin: 10,
            }}
          >
            <MaterialIcons name="error-outline" size={24} color={modalTheme.iconBadgeColor} />
            <Text style={{ marginTop: 8, color: modalTheme.subtleText, fontSize: responsiveFontSize(12) }}>
              {t('Component Failed to Load')}
            </Text>
            <Pressable onPress={this.handleReload} style={{ marginTop: 8 }}>
              <Text style={{ color: modalTheme.primary, fontWeight: '600', fontSize: responsiveFontSize(12) }}>
                {t('Try Again')}
              </Text>
            </Pressable>
          </View>

          {/* Unmount when dismissed so no leftover backdrop blocks the app */}
          {this.state.modalVisible && (
            <View style={{ flex: 1, justifyContent: 'center' }}>
              {/* Dark overlay backdrop */}
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }}
              />
              <Modal
                isVisible
                animationIn="fadeInUp"
                animationOut="fadeOutDown"
                animationInTiming={400}
                animationOutTiming={300}
                backdropOpacity={0.5}
                backdropColor="#000"
                onBackdropPress={this.handleDismiss}
                onBackButtonPress={this.handleDismiss}
                style={{
                  margin: 0,
                  justifyContent: 'center',
                }}
                deviceWidth={screenWidth}
                deviceHeight={screenHeight}
                useNativeDriverForBackdrop={true}
              >
                <View
                  style={{
                    backgroundColor: modalTheme.modalSurface,
                    borderRadius: 24,
                    maxHeight: screenHeight * 0.85,
                    marginHorizontal: 20,
                    overflow: 'hidden',
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.15,
                    shadowRadius: 16,
                    maxWidth: Platform.select({ web: "60%" }),
                    elevation: 20,
                    alignSelf: 'center'
                  }}
                >
              {/* Header */}
              <View
                style={{
                  backgroundColor: modalTheme.panelSurface,
                  paddingHorizontal: 20,
                  paddingVertical: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: modalTheme.panelBorder,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <View
                  style={{
                    backgroundColor: modalTheme.iconBadgeSurface,
                    borderRadius: 12,
                    padding: 10,
                  }}
                >
                  <MaterialIcons name="error-outline" size={20} color={modalTheme.iconBadgeColor} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      fontSize: responsiveFontSize(18),
                      fontWeight: '700',
                      color: modalTheme.titleText,
                      marginBottom: 4,
                    }}
                  >
                    {isCSSStyleError ? t('Style Rendering Error') : t('Component Error')}
                  </Text>
                  <Text
                    style={{
                      fontSize: responsiveFontSize(13),
                      color: modalTheme.subtleText,
                      fontWeight: '500',
                      lineHeight: 18,
                    }}
                  >
                    {t('An error occurred while rendering this component.')}
                  </Text>
                </View>
                <Pressable
                  onPress={this.handleDismiss}
                  hitSlop={12}
                  accessibilityRole="button"
                  accessibilityLabel={t('Close')}
                  style={{
                    padding: 8,
                    borderRadius: 20,
                    backgroundColor: modalTheme.mutedSurface,
                  }}
                >
                  <MaterialIcons name="close" size={20} color={modalTheme.mutedText} />
                </Pressable>
                  </View>
              {/* Content */}
              <ScrollView
                style={{ flex: 0 }}
                contentContainerStyle={{ padding: 20 }}
                showsVerticalScrollIndicator={false}
              >
                <View
                  style={{
                    backgroundColor: modalTheme.panelSurface,
                    borderRadius: 16,
                    padding: 16,
                    borderWidth: 1,
                    borderColor: modalTheme.panelBorder,
                  }}
                >
                  <Text
                    style={{
                      color: modalTheme.bodyText,
                      fontWeight: '600',
                      fontSize: responsiveFontSize(13),
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 12,
                    }}
                  >
                    {t('Error Details')}
                  </Text>
                  <Text
                    style={{
                      fontSize: responsiveFontSize(14),
                      color: modalTheme.bodyText,
                      lineHeight: 20,
                      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
                    }}
                  >
                    {isCSSStyleError
                      ? t('A style rendering issue occurred. Reloading the component should fix it.')
                      : `${id ? `[${id}] ` : ''}${errorMessage}`}
                  </Text>
                </View>
                  </ScrollView>
              {/* Footer Actions */}
              <View
                style={{
                  backgroundColor: modalTheme.panelSurface,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  borderTopWidth: 1,
                  borderTopColor: modalTheme.panelBorder,
                  flexDirection: 'row',
                  gap: 12,
                  justifyContent: 'flex-end',
                }}
              >
                <Pressable
                  onPress={this.handleDismiss}
                  hitSlop={8}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: modalTheme.mutedSurface,
                    minWidth: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    style={{
                      color: modalTheme.bodyText,
                      fontWeight: '600',
                      fontSize: responsiveFontSize(14),
                    }}
                  >
                    {t('Close')}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={this.handleReload}
                  hitSlop={8}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: modalTheme.primary,
                    minWidth: 120,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'row',
                    gap: 8,
                    elevation: 2,
                    shadowColor: modalTheme.primary,
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                  }}
                >
                  <AntDesign name="reload" size={16} color={modalTheme.onPrimary} />
                  <Text
                    style={{
                      color: modalTheme.onPrimary,
                      fontWeight: '600',
                      fontSize: responsiveFontSize(14),
                    }}
                  >
                    {t('Reload Component')}
                  </Text>
                </Pressable>
              </View>
            </View>
          </Modal>
            </View>
          )}
        </>
      );
    }
    return <React.Fragment key={this.state.remountKey}>{this.props.children}</React.Fragment>;
  }
}

function ErrorBoundary(props: ErrorBoundaryProps) {
  const isLightTheme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const modalTheme = getAdaptiveModalTheme(isLightTheme);
  return <ErrorBoundaryInner {...props} isLightTheme={isLightTheme} modalTheme={modalTheme} />;
}

export default ErrorBoundary;
